import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatErr, cx } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useFx } from "../lib/fx";
import { useI18n } from "../lib/i18n";
import { Sparkles, Star, AlertTriangle, MessageSquare, Coins } from "lucide-react";
import OpenDisputeModal from "../components/OpenDisputeModal";
import TripOfferCard from "../components/TripOfferCard";
import ChatDrawer from "../components/ChatDrawer";
import MintRedemptionModal from "../components/MintRedemptionModal";

function ReviewModal({ booking, onClose, onSubmit }) {
  const { t } = useI18n();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!booking) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post("/reviews", { booking_id: booking.id, rating, text });
      onSubmit?.();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/80 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="luxe-card w-full max-w-md p-7 space-y-4" data-testid="review-form">
        <div className="eyebrow">{t("user_dash.leave_review", "Review")}</div>
        <h3 className="section-title text-3xl">{booking.service_title}</h3>
        <div>
          <div className="label-tiny mb-2">{t("user_dash.rating_label", "Your rating")}</div>
          <div className="flex gap-2">
            {[1,2,3,4,5].map(n => (
              <button type="button" key={n} onClick={() => setRating(n)} className={`p-2 ${n <= rating ? "text-champagne" : "text-muted/40"}`} data-testid={`rating-${n}`}>
                <Star size={20} className={n <= rating ? "fill-champagne" : ""}/>
              </button>
            ))}
          </div>
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)}
                  placeholder={t("user_dash.review_placeholder", "How was your experience?")}
                  className="input-bare" data-testid="review-text"/>
        {error && <div className="text-rose-300 text-sm">{error}</div>}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="btn btn-outline flex-1">{t("common.cancel", "Cancel")}</button>
          <button disabled={busy} className="btn btn-primary flex-1" data-testid="review-submit">
            {busy ? t("concierge.sending", "Sending…") : t("user_dash.submit_review", "Submit review")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function UserDashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { format } = useFx();
  const [bookings, setBookings] = useState([]);
  const [trips, setTrips] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [disputeBooking, setDisputeBooking] = useState(null);
  const [chatBookingId, setChatBookingId] = useState(null);
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [offers, setOffers] = useState([]);
  const [flash, setFlash] = useState(null);
  const [mintBooking, setMintBooking] = useState(null);

  // Booking status → { label, chip } using i18n
  const USER_STATUSES = useMemo(() => ({
    requested:                       { label: t("booking_status.requested", "Awaiting provider"),          chip: "warning" },
    pending_user_payment:            { label: t("user_dash.pay_deposit",     "Pay deposit"),                chip: "info" },
    paid_reserved:                   { label: t("booking_status.paid_reserved", "Paid · upcoming"),         chip: "success" },
    completed_confirmed:             { label: t("booking_status.completed_confirmed", "Completed"),         chip: "success" },
    funds_released:                  { label: t("booking_status.funds_released", "Closed"),                 chip: "success" },
    provider_rejected:               { label: t("booking_status.provider_rejected", "Declined"),            chip: "danger" },
    cancelled_by_user:               { label: t("booking_status.cancelled_by_user", "Cancelled"),           chip: "" },
    dispute_refunded:                { label: t("booking_status.dispute_refunded", "Refunded to guest"),    chip: "danger" },
  }), [t]);

  const DISPUTE_ELIGIBLE = ["paid_reserved", "in_progress", "completed_confirmed"];

  const [filter, setFilter] = useState("all");
  const FILTERS = useMemo(() => [
    { id: "all", label: t("user_dash.filter_all", "All") },
    { id: "active", label: t("user_dash.filter_active", "Active"), match: (b) => ["requested","pending_user_payment","paid_reserved","in_progress"].includes(b.status) && !(b.dispute && b.dispute.status === "open") },
    { id: "completed", label: t("user_dash.filter_completed", "Completed"), match: (b) => ["completed_confirmed","funds_released"].includes(b.status) },
    { id: "dispute", label: t("user_dash.filter_dispute", "Disputed"), match: (b) => (b.dispute && b.dispute.status === "open") || b.status === "dispute_refunded" },
    { id: "cancelled", label: t("user_dash.filter_cancelled", "Cancelled"), match: (b) => ["cancelled_by_user","provider_rejected"].includes(b.status) },
  ], [t]);

  const filteredBookings = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter);
    if (!f || !f.match) return bookings;
    return bookings.filter(f.match);
  }, [bookings, filter, FILTERS]);

  const reload = async () => {
    try {
      const [{ data: b }, { data: tt }, { data: of }] = await Promise.all([
        api.get("/bookings/me"),
        api.get("/custom-trips/me"),
        api.get("/custom-trips/offers/me"),
      ]);
      setBookings(b.items); setTrips(tt.items); setOffers(of.items);
    } catch (e) { setError(formatErr(e)); }
  };

  useEffect(() => { reload(); }, []);

  const action = async (id, path) => {
    setBusy(id); setError(null);
    try { await api.post(`/bookings/${id}/${path}`, {}); await reload(); }
    catch (e) { setError(formatErr(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="pt-28 pb-24 px-5 md:px-10 mx-auto max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="eyebrow flex items-center gap-3">
            <span className="role-badge user">Guest</span> {t("common.dashboard", "Dashboard")}
          </div>
          <h1 className="section-title text-4xl md:text-5xl mt-3">
            {t("user_dash.hello", "Hello, {{name}}.", { name: user?.name?.split(" ")[0] || "" })}
          </h1>
          <p className="text-muted text-sm mt-2">{t("user_dash.subtitle", "Your bookings, custom trips and protected deposits — in one place.")}</p>
        </div>
        <Link to="/destinations/ibiza-spain" className="btn btn-primary hidden md:inline-flex" data-testid="dash-explore">
          {t("dashboard.browse_ibiza", "Browse Ibiza")} →
        </Link>
      </div>

      {error && <div className="text-rose-300 text-sm mb-4">{error}</div>}
      {flash && <div className="luxe-card p-4 text-sm text-teal border-teal/40 mb-4" data-testid="user-flash">{flash}</div>}

      {offers.filter(o => o.status === "pending").length > 0 && (
        <div className="mb-12 space-y-3" data-testid="user-pending-offers">
          <h2 className="font-serif-display text-2xl">{t("offer.heading", "Concierge offers")}</h2>
          {offers.filter(o => o.status === "pending").map((o) => (
            <TripOfferCard key={o.id} offer={o} onChange={reload} />
          ))}
        </div>
      )}

      <div className="luxe-card p-5 mb-10 flex items-center justify-between gap-3 flex-wrap" data-testid="concierge-cta">
        <div>
          <div className="eyebrow text-teal">{t("chat.concierge_label", "Concierge desk")}</div>
          <div className="font-serif-display text-xl mt-1">{t("user_dash.concierge_cta_title", "Talk to a real concierge.")}</div>
          <div className="text-sm text-muted mt-1">{t("user_dash.concierge_cta_body", "Direct line to our team for trip planning, custom builds, anything.")}</div>
        </div>
        <button onClick={() => setConciergeOpen(true)} className="btn btn-primary" data-testid="btn-open-concierge">
          <MessageSquare size={14}/> {t("user_dash.open_concierge", "Open concierge chat")}
        </button>
      </div>

      <div className="mb-12">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-serif-display text-2xl">{t("dashboard.tab_bookings", "Bookings")}</h2>
          {bookings.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="booking-filters">
              {FILTERS.map((f) => (
                <button key={f.id}
                        onClick={() => setFilter(f.id)}
                        className={`tab-pill ${filter === f.id ? "active" : ""}`}
                        data-testid={`booking-filter-${f.id}`}>
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {bookings.length === 0 ? (
          <div className="luxe-card p-8 text-center">
            <Sparkles className="mx-auto text-teal mb-2"/>
            <div className="font-serif-display text-2xl">{t("user_dash.no_bookings_title", "No bookings yet.")}</div>
            <div className="text-sm text-muted mt-1 max-w-md mx-auto">{t("user_dash.no_bookings_body", "Browse Ibiza or describe your dream trip — our concierge will curate the rest.")}</div>
            <div className="flex gap-2 justify-center mt-6">
              <Link to="/destinations/ibiza-spain" className="btn btn-primary">{t("dashboard.browse_ibiza", "Browse Ibiza")}</Link>
              <Link to="/custom-trip" className="btn btn-outline">{t("header.nav_custom_trip", "Custom trip")}</Link>
            </div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="luxe-card p-8 text-center text-muted text-sm" data-testid="booking-empty-filter">
            {t("user_dash.no_match_filter", "No bookings match this filter.")}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((b) => {
              const s = USER_STATUSES[b.status] || { label: b.status, chip: "" };
              const inDispute = b.dispute && b.dispute.status === "open";
              const canDispute = DISPUTE_ELIGIBLE.includes(b.status) && !inDispute;
              return (
                <div key={b.id} className="luxe-card p-5" data-testid={`booking-${b.id}`}>
                  <div className="flex items-start gap-4">
                    {b.service_image && <img src={b.service_image} alt="" className="h-20 w-20 rounded-xl object-cover"/>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`chip ${s.chip}`}>{s.label}</span>
                        {inDispute && (
                          <span className="chip danger flex items-center gap-1" data-testid={`dispute-chip-${b.id}`}>
                            <AlertTriangle size={10} /> {t("dispute.open_chip", "In dispute")}
                          </span>
                        )}
                        {b.dispute && b.dispute.status === "resolved" && (
                          <span className="chip" data-testid={`dispute-resolved-chip-${b.id}`}>
                            {t("dispute.resolved_chip", "Resolved")}
                          </span>
                        )}
                        <span className="text-xs text-muted font-mono">#{b.id.slice(0,8)}</span>
                      </div>
                      <div className="font-serif-display text-xl truncate">{b.service_title}</div>
                      <div className="text-xs text-muted mt-1">
                        {b.start_date} · {t("common.guests", "{{count}} guests", { count: b.guests })} · {format(b.price_total, b.currency)}
                      </div>
                      {inDispute && b.dispute.reason && (
                        <div className="text-xs text-rose-200/80 mt-2 italic line-clamp-2" data-testid={`dispute-reason-${b.id}`}>
                          "{b.dispute.reason}"
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {b.status === "pending_user_payment" && (
                      <>
                        <button disabled={busy === b.id} onClick={() => action(b.id, "pay")} className="btn btn-primary text-[12px]" data-testid={`btn-pay-${b.id}`}>
                          {t("user_dash.pay_deposit", "Pay deposit")} · {format(b.deposit_required, b.currency)}
                        </button>
                        {!b.mint_applied && (
                          <button disabled={busy === b.id} onClick={() => setMintBooking(b)} className="btn btn-outline text-[12px]" data-testid={`btn-redeem-mint-${b.id}`}>
                            <Coins size={12}/> {t("user_dash.redeem_mint", "Use MINT points")}
                          </button>
                        )}
                        {b.mint_applied > 0 && (
                          <span className="chip success text-[11px]" data-testid={`mint-applied-${b.id}`}>
                            {t("user_dash.mint_applied_chip", "−{{amount}} via {{points}} MINT", {
                              amount: format(b.mint_discount_native || 0, b.currency),
                              points: b.mint_applied,
                            })}
                          </span>
                        )}
                      </>
                    )}
                    {b.status === "paid_reserved" && (
                      <button disabled={busy === b.id} onClick={() => action(b.id, "complete")} className="btn btn-primary text-[12px]" data-testid={`btn-complete-${b.id}`}>
                        {t("user_dash.confirm_completed", "Confirm completion")}
                      </button>
                    )}
                    {(b.status === "completed_confirmed" || b.status === "funds_released") && (
                      <button onClick={() => setReviewBooking(b)} className="btn btn-outline text-[12px]" data-testid={`btn-review-${b.id}`}>
                        {t("user_dash.leave_review", "Leave review")}
                      </button>
                    )}
                    {["requested", "pending_user_payment"].includes(b.status) && (
                      <button disabled={busy === b.id} onClick={() => action(b.id, "cancel")} className="btn btn-ghost text-[12px] text-rose-300" data-testid={`btn-cancel-${b.id}`}>
                        {t("user_dash.cancel_booking", "Cancel")}
                      </button>
                    )}
                    {canDispute && (
                      <button onClick={() => setDisputeBooking(b)} className="btn btn-ghost text-[12px] text-rose-300 ml-auto" data-testid={`btn-dispute-${b.id}`}>
                        <AlertTriangle size={12} /> {t("dispute.open_btn", "Open dispute")}
                      </button>
                    )}
                    {!["cancelled_by_user", "provider_rejected"].includes(b.status) && (
                      <button onClick={() => setChatBookingId(b.id)}
                              className={cx("btn btn-ghost text-[12px]", canDispute ? "" : "ml-auto")}
                              data-testid={`btn-message-${b.id}`}>
                        <MessageSquare size={12}/> {t("user_dash.message_provider", "Message provider")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-serif-display text-2xl mb-4">{t("dashboard.tab_trips", "Custom trip requests")}</h2>
        {trips.length === 0 ? (
          <div className="luxe-card p-6 text-sm text-muted">
            {t("user_dash.no_trips_title", "No custom trips yet.")}{" "}
            <Link to="/custom-trip" className="text-teal">{t("dashboard.create_custom_trip", "Plan one")} →</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {trips.map((trip) => (
              <div key={trip.id} className="luxe-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`trip-${trip.id}`}>
                <div>
                  <div className="font-serif-display text-xl">{trip.destinations?.join(" · ") || t("header.nav_custom_trip", "Custom")}</div>
                  <div className="text-xs text-muted">
                    {t("common.guests", "{{count}} guests", { count: trip.guests })} · {trip.start_date || t("custom_trip.flexible", "flexible")} · {(trip.budget_min||0)}–{(trip.budget_max||0)} {trip.currency}
                  </div>
                </div>
                <span className="chip warning">{trip.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <ReviewModal booking={reviewBooking} onClose={() => setReviewBooking(null)} onSubmit={() => { setReviewBooking(null); reload(); }}/>

      {disputeBooking && (
        <OpenDisputeModal
          booking={disputeBooking}
          onClose={(opened) => {
            setDisputeBooking(null);
            if (opened) {
              setFlash(t("dispute.submitted_flash", "Dispute submitted. Our concierge will review within 24 hours."));
              setTimeout(() => setFlash(null), 5000);
              reload();
            }
          }}
        />
      )}

      {chatBookingId && (
        <ChatDrawer bookingId={chatBookingId} onClose={() => setChatBookingId(null)} />
      )}
      {conciergeOpen && (
        <ChatDrawer kind="concierge" onClose={() => setConciergeOpen(false)} />
      )}
      {mintBooking && (
        <MintRedemptionModal
          booking={mintBooking}
          onClose={(applied) => {
            setMintBooking(null);
            if (applied) {
              setFlash(t("user_dash.mint_applied_flash", "MINT points applied — your deposit was reduced."));
              setTimeout(() => setFlash(null), 5000);
              reload();
            }
          }}
        />
      )}
    </div>
  );
}
